import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";

const packages = [
  {
    name: "Starter Mode",
    tagline: "Beginner-friendly transformation",
    price: "1,499",
    period: "/month",
    features: [
      "Full gym access (5AM–12AM)",
      "Beginner training program",
      "Form & technique coaching",
      "Locker room access",
      "1 free group class/week",
    ],
    popular: false,
  },
  {
    name: "Core Build",
    tagline: "Muscle gain & aesthetics",
    price: "2,999",
    period: "/month",
    features: [
      "Everything in Starter Mode",
      "Hypertrophy training program",
      "Nutrition guidance",
      "Progress tracking",
      "Unlimited group classes",
      "Recovery suite access",
    ],
    popular: true,
  },
  {
    name: "Shred Season",
    tagline: "Fat loss + conditioning",
    price: "3,499",
    period: "/month",
    features: [
      "Everything in Core Build",
      "Custom fat-loss plan",
      "HIIT & conditioning sessions",
      "Weekly body composition checks",
      "Creator zone access",
      "Priority booking",
    ],
    popular: false,
  },
  {
    name: "Hybrid Grind",
    tagline: "Strength + endurance mastery",
    price: "4,999",
    period: "/month",
    features: [
      "Everything in Shred Season",
      "1-on-1 personal training (4x/mo)",
      "HYROX competition prep",
      "Advanced performance analytics",
      "Guest passes (2/month)",
      "Exclusive member events",
    ],
    popular: false,
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.1 },
  }),
};

export default function PricingSection() {
  return (
    <section id="pricing" data-testid="pricing-section" className="py-20 sm:py-24 lg:py-32 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          custom={0}
        >
          <span className="text-[#F59E0B] text-sm font-semibold tracking-[0.3em] uppercase">Invest in Yourself</span>
        </motion.div>
        <motion.h2
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          custom={1}
          className="font-['Bebas_Neue'] text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white mt-4 mb-6"
        >
          Pricing & <span className="text-[#F59E0B]">Packages</span>
        </motion.h2>
        <motion.p
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          custom={2}
          className="text-[#A3A3A3] text-base mb-16 max-w-lg"
        >
          Choose the program that matches your goals. Every package includes full gym access, 7 days a week.
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {packages.map((pkg, i) => (
            <motion.div
              key={pkg.name}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={fadeUp}
              custom={i + 3}
              data-testid={`pricing-${pkg.name.toLowerCase().replace(/\s/g, '-')}`}
              className={`relative flex flex-col p-6 border transition-all duration-500 hover:border-[#F59E0B]/60 ${
                pkg.popular
                  ? "bg-[#F59E0B] border-[#F59E0B] text-black"
                  : "bg-[#121212] border-[#262626]"
              }`}
            >
              {pkg.popular && (
                <div className="absolute -top-3 left-6 px-4 py-1 bg-black text-[#F59E0B] text-xs font-bold tracking-wider uppercase">
                  Most Popular
                </div>
              )}

              <div className="mb-6">
                <h3 className={`font-['Bebas_Neue'] text-2xl tracking-wide ${pkg.popular ? "text-black" : "text-white"}`}>
                  {pkg.name}
                </h3>
                <p className={`text-sm mt-1 ${pkg.popular ? "text-black/70" : "text-[#A3A3A3]"}`}>
                  {pkg.tagline}
                </p>
              </div>

              <div className="mb-6">
                <span className={`font-['Bebas_Neue'] text-5xl tracking-tight ${pkg.popular ? "text-black" : "text-white"}`}>
                  ₹{pkg.price}
                </span>
                <span className={`text-sm ${pkg.popular ? "text-black/60" : "text-[#A3A3A3]"}`}>
                  {pkg.period}
                </span>
              </div>

              <ul className="space-y-3 mb-8 flex-1">
                {pkg.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm">
                    <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${pkg.popular ? "text-black" : "text-[#F59E0B]"}`} />
                    <span className={pkg.popular ? "text-black/90" : "text-[#A3A3A3]"}>{f}</span>
                  </li>
                ))}
              </ul>

              <button
                data-testid={`pricing-cta-${pkg.name.toLowerCase().replace(/\s/g, '-')}`}
                onClick={() => {
                  const el = document.querySelector("#contact");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                className={`w-full py-3 font-semibold text-sm tracking-wider uppercase transition-all duration-300 hover:scale-[1.02] ${
                  pkg.popular
                    ? "bg-black text-[#F59E0B] hover:bg-black/90"
                    : "bg-[#F59E0B] text-black hover:bg-[#D97706]"
                }`}
              >
                Get Started
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
